import { datadogRum } from '@datadog/browser-rum';

import { getDatadogRumConfig } from '@/shared/utils/datadogRumConfig';

type ReportErrorContext = Record<string, unknown>;

/**
 * Reports an error to telemetry (Datadog RUM) when available, always falling back
 * to console.error so the error is never swallowed silently. Defaults to the React
 * error-boundary source; pass `context.source` to report from another call site.
 */
export function reportError(
  error: Error & { digest?: string },
  context?: ReportErrorContext,
): void {
  const errorContext: ReportErrorContext = {
    source: 'react-error-boundary',
    digest: error.digest,
    ...context,
  };

  // Always log to console so the error surfaces locally and in environments
  // where Datadog RUM is not configured.
  console.error(`[${String(errorContext.source)}]`, errorContext, error);

  if (typeof window === 'undefined') {
    return;
  }

  if (!getDatadogRumConfig()) {
    return;
  }

  try {
    datadogRum.addError(error, errorContext);
  } catch {
    // Datadog RUM may not be initialized yet (e.g. error during early render).
    // The console.error above already captured the error, so we intentionally
    // ignore the failure to avoid masking the original error.
  }
}
